import { cloneDeep } from 'lodash';
import { CareEnvironment, FundingPolicyType } from '../../appModel';
import {
  defaultsRetroactiveWaitingPeriodReimbursement,
  isCareMattersFamily,
} from '../../FundingPolicyTypeDef';
import { getBenefitPaymentCapabilities } from '../../benefitPaymentCapabilities';
import { calcClientDerivatives } from '../calcClientDerivatives';
import {
  resolveFacilityCareWaitingPeriodDays,
  resolveHomeCareWaitingPeriodDays,
} from '../funding/resolveQuoteWaitingPeriodDays';

type Quote = PolicyQuote & {
  projectedCostCoverage?: number;
  discountSavings?: number;
  discountPolicyType?: number;
  roi?: number;
  roiValues?: {
    projectedReturnOnInvestment: number;
    projectedCompoundAnnualGrowthRate: number;
    projectedInternalRateOfReturn: number | undefined;
    investmentPeriodYears: number;
  };
  formattedROI?: string;
  illustrationPdf?: Blob;
  isCreatingIllustration?: boolean;
  illustrationError?: string | null;
  updatedQuote?: Quote | null;
  illustrationPdfUrl?: string | null;
  responseData?: any;
};

export const POLICY_PRODUCT_NAME_MAPPING = {
  'Asset Care': FundingPolicyType.assetCare,
  'CareMatters® II': FundingPolicyType.careMattersTwo,
  'CareMatters® Together': FundingPolicyType.jointCareMatters,
  'CareScout Care Assurance': FundingPolicyType.careScoutCareAssurance,
  'CareScout': FundingPolicyType.careScoutCareAssurance, // Add shorter product name
  'MoneyGuard Fixed Advantage® (2025)': FundingPolicyType.moneyGuardFixedAdvantage,
  'SecureCare III': FundingPolicyType.secureCareThree,
  'SecureCare IV': FundingPolicyType.secureCareFour,
  'Bridge®': FundingPolicyType.equitrustBridge,
  'Bridge': FundingPolicyType.equitrustBridge,
  'Asset Flex': FundingPolicyType.nylAssetFlex,
  'AssetFlex': FundingPolicyType.nylAssetFlex,
} as const;

export const getPolicyTypeFromProductName = (
  productName: string,
  isJointPolicy: boolean = false,
): FundingPolicyType | undefined => {
  if (productName === 'Asset Care') {
    return isJointPolicy ? FundingPolicyType.jointAssetCare : FundingPolicyType.assetCare;
  }
  return POLICY_PRODUCT_NAME_MAPPING[productName as keyof typeof POLICY_PRODUCT_NAME_MAPPING];
};

const getInflationRate = (quote: Quote): number => {
  if (quote.aobInflation && !isNaN(parseFloat(quote.aobInflation))) {
    return parseFloat(quote.aobInflation);
  }

  const inflationOption = quote.inflationBenefitOption;
  if (!inflationOption || inflationOption === 'None') {
    return 0;
  }

  const match = inflationOption?.match(/(\d+(\.\d+)?)/);
  if (match && match[0]) {
    return parseFloat(match[0]);
  }

  return 0;
};

const getInflationProtectionYears = (quote: Quote): number | null => {
  if (quote.inflationBenefitOption) {
    const yearsMatch = quote.inflationBenefitOption.match(/for\s+(\d+)\s+years?/i);
    if (yearsMatch && yearsMatch[1]) {
      return parseInt(yearsMatch[1], 10);
    }
  }
  // Fall back to original logic using cobInflation and aobInflation
  if (
    !quote.cobInflation ||
    quote.cobInflation === '0' ||
    !quote.aobInflation ||
    quote.aobInflation === '0'
  ) {
    return null;
  }

  return 20;
};

// Asset Flex quotes carry the AOB / EOB benefit periods as unit-bearing
// strings: "N Years", "N Months", "N/A" (no Extension pool), or "Lifetime".
// Convert to whole months so the projection's duration model can size each pool
// from monthly benefit × duration; without these the strategy falls back to
// legacy dollar pools. The unit MUST be honored — "25 Months" is 25, not 25×12.
// "Lifetime"/unparseable returns null (the duration model can't size an
// unbounded pool, so it falls back to the stored dollar pool).
const parseBenefitDurationMonths = (value: string | undefined): number | null => {
  if (!value) return null;
  const v = value.trim();
  if (/n\/a|none/i.test(v)) return 0;
  if (/lifetime|unlimited/i.test(v)) return null;
  const num = parseFloat(v);
  if (isNaN(num) || num <= 0) return null;
  if (/month/i.test(v)) return Math.round(num);
  // "N Years" (or a bare number — the carrier source prints years).
  return Math.round(num * 12);
};

const calculatePolicyInflationProtection = (
  inflationRate: number,
  aobInflation: string | undefined,
  cobInflation: string | undefined,
): number | null => {
  if (inflationRate <= 0) {
    return null;
  }

  // Parse AOB and COB, treating "None", "0", empty strings as undefined
  const parseInflationValue = (value: string | undefined): number | undefined => {
    if (!value || value === 'None' || value === '0') return undefined;
    const parsed = parseFloat(value);
    return isNaN(parsed) || parsed === 0 ? undefined : parsed;
  };

  const aobInflationParsed = parseInflationValue(aobInflation);
  const cobInflationParsed = parseInflationValue(cobInflation);

  // If both AOB and COB are defined (regardless of whether they match), use inflationRate
  if (aobInflationParsed !== undefined && cobInflationParsed !== undefined) {
    return inflationRate / 100;
  }

  // If neither are defined, use inflationRate from inflationBenefitOption
  if (aobInflationParsed === undefined && cobInflationParsed === undefined) {
    return inflationRate / 100;
  }
  return null;
};

export const createPolicyFundingSourceFromQuote = (
  quote: Quote,
  isAdvisorSession = false,
  advisorId?: string,
  updatedQuote?: Quote | null,
  illustrationPdfUrl?: string | null,
): PolicyFundingSource => {
  const calculateDailyBenefit = (quote: Quote): number => {
    if (
      quote.ltcBenefitMonthlyIndividual &&
      !isNaN(quote.ltcBenefitMonthlyIndividual) &&
      quote.ltcBenefitMonthlyIndividual > 0
    ) {
      return quote.ltcBenefitMonthlyIndividual / 30;
    }

    // Fallback: try using annual benefit if available
    if (
      quote.ltcBenefitAnnualIndividual &&
      !isNaN(quote.ltcBenefitAnnualIndividual) &&
      quote.ltcBenefitAnnualIndividual > 0
    ) {
      console.warn(
        `Using annual benefit for daily calculation for quote ${quote.insuranceQuoteId}`,
      );
      return quote.ltcBenefitAnnualIndividual / 365;
    }

    // Fallback: estimate from total LTC benefit
    // For most policies, assume 4-6 year benefit period as reasonable default
    if (quote.ltcBenefitTotal && !isNaN(quote.ltcBenefitTotal) && quote.ltcBenefitTotal > 0) {
      // Estimate 5-year benefit period: total benefit / (5 years * 365 days)
      const FIVE_YEARS_IN_DAYS = 1825;
      const estimatedDailyBenefit = quote.ltcBenefitTotal / FIVE_YEARS_IN_DAYS;
      console.warn(
        `ltcBenefitMonthlyIndividual missing for quote ${quote.insuranceQuoteId}. Estimated daily benefit from total: $${estimatedDailyBenefit.toFixed(2)}/day`,
      );
      return estimatedDailyBenefit;
    }

    // Final fallback: set to 0 to prevent NaN
    console.error(
      `Unable to calculate daily benefit for quote ${quote.insuranceQuoteId}. Setting to 0.`,
    );
    return 0;
  };

  const dailyBenefit = calculateDailyBenefit(quote);

  const isJointPolicy = quote.policyType === 'Joint Life';
  const policyType = getPolicyTypeFromProductName(quote.productName, isJointPolicy);
  const isSimpleInflation = quote.inflationBenefitOption?.toLowerCase().includes('simple') ?? false;

  const getIsIndemnityPolicyPayment = (quote: Quote): boolean =>
    getBenefitPaymentCapabilities(quote).default === 'indemnity';

  // Check if this is an unlimited policy (ltcBenefitTotal = 0 indicates unlimited)
  const isUnlimitedPolicy = quote.ltcBenefitTotal === 0;

  const inflationRate = getInflationRate(quote);
  const inflationProtectionYears = getInflationProtectionYears(quote);
  const aobInflationParsed =
    quote.aobInflation && quote.aobInflation !== 'None' ? parseFloat(quote.aobInflation) : null;
  const cobInflationParsed =
    quote.cobInflation && quote.cobInflation !== 'None' ? parseFloat(quote.cobInflation) : null;
  // Asset Flex and Asset Care both size their AOB / COB pools from monthly
  // benefit × benefit period, so map the quote's duration strings into the
  // duration fields the projection's duration model reads. ("N/A" COB → 0 so an
  // un-elected EOB rider still yields a clean duration model; "Lifetime" → null
  // so the legacy/unlimited pool handling takes over.)
  const usesDurationDerivedPools =
    policyType === FundingPolicyType.nylAssetFlex ||
    policyType === FundingPolicyType.assetCare ||
    policyType === FundingPolicyType.jointAssetCare;
  const accelerationBenefitDurationMonths = usesDurationDerivedPools
    ? parseBenefitDurationMonths(quote.aobDuration)
    : null;
  const eobBenefitDurationMonths = usesDurationDerivedPools
    ? parseBenefitDurationMonths(quote.cobDuration)
    : null;

  // Check if this is a 10-Pay quote
  const is10PayQuote = quote.cobPaymentOption === '10-Pay';

  const basicPolicyFundingSource: PolicyFundingSource = {
    hasPolicyFunding: true,
    policyType,
    isSimpleInflationProtection: isSimpleInflation,
    isIndemnityPolicyPayment: getIsIndemnityPolicyPayment(quote),
    policyBenefitIsUnlimited: isUnlimitedPolicy,
    policyTotalLTCBenefit: isUnlimitedPolicy ? null : quote.ltcBenefitTotal,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
    addedViaShopInsurance: true,
    insuranceQuoteId: quote.insuranceQuoteId,
    policyLumpSumPayment: is10PayQuote ? null : quote.premiumAmount,
    policyPremiumMonthlyCost: is10PayQuote ? quote.premiumAmount / 12 : 0,
    policyPremiumStartYear: new Date().getFullYear(),
    policyMaximumBenefitAmount: quote.deathBenefitInitial,
    dailyHomeBenefitAmount: dailyBenefit,
    dailyHomeInflationProtectionPercent: inflationRate / 100,
    dailyAssistedLivingBenefitAmount: dailyBenefit,
    dailyAssistedLivingInflationProtectionPercent: inflationRate / 100,
    dailyIndependentLivingBenefitAmount: dailyBenefit,
    dailyIndependentLivingInflationProtectionPercent: inflationRate / 100,
    dailyNursingHomeBenefitAmount: dailyBenefit,
    dailyNursingHomeInflationProtectionPercent: inflationRate / 100,
    homeCareWaitingPeriodDays: resolveHomeCareWaitingPeriodDays(quote),
    facilityCareWaitingPeriodDays: resolveFacilityCareWaitingPeriodDays(quote),
    hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(
      policyType,
    )
      ? true
      : null,
    policyLimitedPayYears: is10PayQuote ? 10 : null,
    policyBenefitPeriodMonths: isCareMattersFamily(policyType)
      ? (quote.ltcBenefitDurationMonths ?? null)
      : null,
    policyAccelerationBenefitDurationMonths: accelerationBenefitDurationMonths,
    policyEobBenefitDurationMonths: eobBenefitDurationMonths,
    policyMonthlyWithdrawalRate: null,
    policyPremiumIncreaseRate: null,
    policyPremiumIncreaseYears: null,
    policyInflationProtection: calculatePolicyInflationProtection(
      inflationRate,
      quote.aobInflation,
      quote.cobInflation,
    ),
    policyInflationProtectionOnFaceAmount: aobInflationParsed
      ? aobInflationParsed / 100
      : cobInflationParsed !== null
        ? 0
        : null,
    policyInflationProtectionOnCOBAmount: cobInflationParsed ? cobInflationParsed / 100 : null,
    policyInflationProtectionYears: inflationProtectionYears,
    policyBenefitUtilizedToDate: 0,
    policyContinuationBenefitAmount: isUnlimitedPolicy
      ? null
      : quote.ltcBenefitTotal - quote.deathBenefitInitial,
    policyMinimumGuaranteedBenefitAmount: quote.deathBenefitMinimumGuaranteedIndividual ?? null,
    customPolicyId: null,
    sourceQuote: quote,
    updatedQuote: updatedQuote || null,
    illustrationPdfUrl: illustrationPdfUrl || null,
    // Include ROI values from updatedQuote if available
    policyProjectedReturnOnInvestment:
      updatedQuote?.roiValues?.projectedReturnOnInvestment ?? undefined,
    policyProjectedCompoundAnnualGrowthRate:
      updatedQuote?.roiValues?.projectedCompoundAnnualGrowthRate ?? undefined,
    policyProjectedInternalRateOfReturn:
      updatedQuote?.roiValues?.projectedInternalRateOfReturn ?? undefined,
    investmentPeriodYears: updatedQuote?.roiValues?.investmentPeriodYears ?? undefined,
  } as PolicyFundingSource;

  return basicPolicyFundingSource;
};

export const createUpdatedQuoteFromResponse = (
  sourceQuote: Quote,
  responseData: { [key: string]: string | null } | null,
): Quote => {
  if (!responseData) {
    return sourceQuote;
  }

  // Parse monetary values from formatted strings (e.g., "$6,855" -> 6855)
  const parseMonetaryValue = (value: string | null): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/[$,]/g, '')) || 0;
  };

  const updatedQuote: Quote = {
    ...sourceQuote,
    premiumAmount: responseData.premiumAmount
      ? parseMonetaryValue(responseData.premiumAmount)
      : sourceQuote.premiumAmount,

    ltcBenefitMonthlyIndividual: responseData.initialMonthlyBenBase
      ? parseMonetaryValue(responseData.initialMonthlyBenBase)
      : sourceQuote.ltcBenefitMonthlyIndividual,

    ltcBenefitAnnualIndividual: responseData.initialAnnualBenBase
      ? parseMonetaryValue(responseData.initialAnnualBenBase)
      : sourceQuote.ltcBenefitAnnualIndividual,

    deathBenefitInitial: responseData.deathBenefit
      ? parseMonetaryValue(responseData.deathBenefit)
      : sourceQuote.deathBenefitInitial,

    ltcBenefitTotal:
      responseData.totalInitialLTC === 'Unlimited'
        ? 0 // 0 indicates unlimited in our system
        : responseData.totalInitialLTC
          ? parseMonetaryValue(responseData.totalInitialLTC)
          : sourceQuote.ltcBenefitTotal,

    aobInflation: responseData.aobInflOption || sourceQuote.aobInflation,
    cobInflation: responseData.cobInflOption || sourceQuote.cobInflation,
    inflationDuration: responseData.inflDuration || sourceQuote.inflationDuration,
  };

  return updatedQuote;
};

export const calculateROI = (calculatedPolicy: PolicyFundingSource, calculationType = 'total') => {
  let roi = 0;

  switch (calculationType) {
    case 'compoundAnnualGrowthRate':
      roi = calculatedPolicy.policyProjectedCompoundAnnualGrowthRate || 0;
      break;
    case 'internalRateOfReturn':
      roi = calculatedPolicy.policyProjectedInternalRateOfReturn || 0;
      break;
    case 'total':
    default:
      roi = calculatedPolicy.policyProjectedReturnOnInvestment || 0;
  }

  return roi;
};

function findDiscountPolicyType(
  clientLocalCareEnvironmentCosts?: LocalCareEnvironmentCosts,
): number | undefined {
  if (!clientLocalCareEnvironmentCosts) {
    return undefined;
  }

  const careEnvironments: CareEnvironment[] = [
    CareEnvironment.home,
    CareEnvironment.assistedLiving,
    CareEnvironment.fullCareFacility,
    CareEnvironment.independentLiving,
  ];

  for (const careEnvironment of careEnvironments) {
    const environmentCost = clientLocalCareEnvironmentCosts[careEnvironment];
    if (environmentCost?.discount?.policyType) {
      return environmentCost.discount.policyType;
    }
  }

  return undefined;
}

const getPremiumCostPerMonth = (quote: TraditionalLTCQuote): number => {
  switch (quote.premiumFrequency) {
    case 'Monthly':
      return quote.primaryModalPremium;
    case 'Annually':
      return quote.primaryModalPremium / 12;
    default:
      return quote.primaryModalPremium / 12;
  }
};

export const parseBioPercentToInflationRate = (bioPercent: string): number => {
  return bioPercent === 'No BIO' ? 0 : parseFloat(bioPercent.match(/(\d+)%/)?.[1] || '0') / 100;
};

function calcMaxAchievableCoverage(
  updatedClient: Client,
  fundingSourceId: string,
  projectedCostCoverage: number,
): number {
  const phaseCalculations = updatedClient.policyPhaseCalculations?.[fundingSourceId];
  return (
    phaseCalculations?.reduce(
      (sum, phase) => sum + (phase.portionOfPhaseCostBenefitEligible ?? 0),
      0,
    ) ?? projectedCostCoverage
  );
}

export const createQuoteWithROIandProjectedCoverage = (
  quote: Quote,
  client: Client,
  calculationType = 'total',
) => {
  const basicPolicy = createPolicyFundingSourceFromQuote(quote);
  const tempFundingSourceId = `temp-quote-preview-${quote.insuranceQuoteId}`;
  const isJointPolicy = quote.policyType === 'Joint Life';

  // Only Joint Life policies read the partner's computed coverage. The partner
  // carries 600KB+ of invariant claim projections, so excluding it from the
  // per-quote clone for non-joint quotes (~95% of them) avoids deep-cloning and
  // re-deriving it on every quote — the dominant cost when enriching large sets.
  // (Couples Discount only needs the *presence* of a partner, which comes from
  // marital fields on the client, not from this object.)
  const clientClone = cloneDeep(
    isJointPolicy ? client : { ...client, mutableClientPartner: null },
  );

  clientClone.multipleFundingSources.ltcPolicy = {
    ...clientClone.multipleFundingSources.ltcPolicy,
    [tempFundingSourceId]: basicPolicy,
  };

  const updatedClientWithDerivatives = calcClientDerivatives(clientClone, null);

  const policy = updatedClientWithDerivatives.multipleFundingSources.ltcPolicy[tempFundingSourceId];
  const roi = calculateROI(policy, calculationType);
  // Use `??` instead of `||` so a legitimately-computed 0 isn't treated as
  // "missing". Falsy-zero with `||` was causing joint quotes whose partner
  // coverage came back as 0 to fall through and end up `undefined`, which
  // then hid the "You / Partner" breakdown on the quote card.
  const projectedCostCoverage =
    policy.projectedCostCoverage ?? policy.policyCoverageOfRemainingLtcCosts ?? 0;

  let partnerProjectedCostCoverage: number | undefined = undefined;
  if (isJointPolicy) {
    const partnerPolicy =
      updatedClientWithDerivatives.mutableClientPartner?.fundingSources.ltcPolicy;
    partnerProjectedCostCoverage =
      partnerPolicy?.projectedCostCoverage ?? partnerPolicy?.policyCoverageOfRemainingLtcCosts;
  }
  const maxAchievableCoverage = calcMaxAchievableCoverage(
    updatedClientWithDerivatives,
    tempFundingSourceId,
    projectedCostCoverage,
  );

  const discountSavings =
    updatedClientWithDerivatives.allPhaseCosts
      ?.allPhaseInflatedProfessionalShareCostDiscountAmount || 0;

  const discountPolicyType = findDiscountPolicyType(
    updatedClientWithDerivatives.clientLocalCareEnvironmentCosts,
  );

  const roiValues = {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment || 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate || 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears || 0,
  };

  return {
    ...quote,
    roi,
    roiValues,
    projectedCostCoverage,
    partnerProjectedCostCoverage,
    maxAchievableCoverage,
    discountSavings,
    discountPolicyType,
  };
};

export const createPolicyFundingSourceFromTraditionalLTCQuote = (
  quote: TraditionalLTCQuote,
  isAdvisorSession = false,
  advisorId?: string,
): PolicyFundingSource => {
  const inflationRate = parseBioPercentToInflationRate(quote.bioPercent);
  const policyType = getPolicyTypeFromProductName(quote.productName, false);

  const basicPolicy = {
    carrierName: quote.carrier,
    hasPolicyFunding: true,
    isSimpleInflationProtection: false,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
    isIndemnityPolicyPayment: false,
    policyBenefitIsUnlimited: false,
    policyMaximumBenefitAmount: quote.benefitPool,
    policyPremiumMonthlyCost: getPremiumCostPerMonth(quote),
    policyPremiumStartYear: new Date().getFullYear(),
    policyInflationProtection: inflationRate,
    policyLumpSumPayment: null,
    dailyHomeBenefitAmount: quote.dailyBenefitAmount,
    dailyHomeInflationProtectionPercent: null,
    dailyIndependentLivingBenefitAmount: quote.dailyBenefitAmount,
    dailyIndependentLivingInflationProtectionPercent: null,
    dailyAssistedLivingBenefitAmount: quote.dailyBenefitAmount,
    dailyAssistedLivingInflationProtectionPercent: null,
    dailyNursingHomeBenefitAmount: quote.dailyBenefitAmount,
    dailyNursingHomeInflationProtectionPercent: null,
    homeCareWaitingPeriodDays: quote.eliminationPeriod,
    facilityCareWaitingPeriodDays: quote.eliminationPeriod,
    hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(
      policyType,
    )
      ? true
      : null,
    policyLimitedPayYears: null,
    policyBenefitPeriodMonths: null,
    policyMonthlyWithdrawalRate: null,
    policyPremiumIncreaseRate: null,
    policyPremiumIncreaseYears: null,
    policyInflationProtectionOnFaceAmount: null,
    policyInflationProtectionOnCOBAmount: null,
    policyInflationProtectionYears: null,
    policyBenefitUtilizedToDate: 0,
    policyContinuationBenefitAmount: null,
    policyMinimumGuaranteedBenefitAmount: null,
    customPolicyId:
      quote.traditionalLTCQuoteId != null ? String(quote.traditionalLTCQuoteId) : undefined,
    addedViaShopInsurance: false,
    insuranceQuoteId:
      quote.traditionalLTCQuoteId != null ? String(quote.traditionalLTCQuoteId) : undefined,
    sourceQuote: quote,
    policyType,
  };

  return basicPolicy as PolicyFundingSource;
};

export const createTraditionalLTCQuoteWithROIandProjectedCoverage = (
  quote: TraditionalLTCQuote,
  client: Client,
  calculationType = 'total',
) => {
  const basicPolicy = createPolicyFundingSourceFromTraditionalLTCQuote(quote);
  const tempFundingSourceId = `temp-trad-ltc-preview-${quote.traditionalLTCQuoteId}`;

  // Traditional LTC quotes never use the partner; exclude its large invariant
  // projection data from the per-quote clone.
  const clientClone = cloneDeep({ ...client, mutableClientPartner: null });

  clientClone.multipleFundingSources.ltcPolicy = {
    [tempFundingSourceId]: basicPolicy,
  };

  const updatedClientWithDerivatives = calcClientDerivatives(clientClone, null);

  const policy = updatedClientWithDerivatives.multipleFundingSources.ltcPolicy[tempFundingSourceId];
  const roi = calculateROI(policy, calculationType);
  const projectedCostCoverage =
    policy.projectedCostCoverage || policy.policyCoverageOfRemainingLtcCosts || 0;

  const maxAchievableCoverage = calcMaxAchievableCoverage(
    updatedClientWithDerivatives,
    tempFundingSourceId,
    projectedCostCoverage,
  );

  const discountSavings =
    updatedClientWithDerivatives.allPhaseCosts
      ?.allPhaseInflatedProfessionalShareCostDiscountAmount || 0;

  const discountPolicyType = findDiscountPolicyType(
    updatedClientWithDerivatives.clientLocalCareEnvironmentCosts,
  );

  const roiValues = {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment || 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate || 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears || 0,
  };

  return {
    ...quote,
    roi,
    roiValues,
    projectedCostCoverage,
    maxAchievableCoverage,
    discountSavings,
    discountPolicyType,
  };
};
